import is from '@sindresorhus/is';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useContext } from 'react';

import { ApplicationContext } from '../../../../../shared/source/context/context';
import {
    paginationCountResponseSchema,
    relatedSkillsResponseSchema,
} from '../../../../../shared/source/data-contract/pagination';
import { globalSearchTermSchema } from '../../../data-contract/global-search';
import { useGlobalPermissions } from '../global-permissions';

import type {
    PaginationCountResponse,
    RelatedSkillsResponse,
} from '../../../../../shared/source/data-contract/pagination';
import type { Pathname } from '../../../../../shared/source/http-fetcher/http-fetcher';
import type { ValidatingHttpFetcherWithBaseUrl } from '../../../../../shared/source/validating-http-fetcher';
import type { UseQueryResult } from '@tanstack/react-query';
import type { z } from 'zod';

type QueryKey = readonly [Pathname, 'countOnly', readonly string[]];

const pathnameSchemaMap = {
    '/global/skill-id/jobs': paginationCountResponseSchema,
    '/global/skill-id/learning-opportunities': paginationCountResponseSchema,
    '/global/skill-id/skills': relatedSkillsResponseSchema,
    '/global/skill-id/users': paginationCountResponseSchema,
} as const;

type PathnameToSchema = typeof pathnameSchemaMap;

type PathnameResponseMap = { readonly [K in keyof PathnameToSchema]: z.infer<PathnameToSchema[K]> };

const createSkillIdsCountQueryFunction =
    <T extends keyof typeof pathnameSchemaMap>(
        pathname: T,
        potentialSkillIds: readonly string[],
        validatingCobrainerApiFetcher: ValidatingHttpFetcherWithBaseUrl,
    ) =>
    async (): Promise<PathnameResponseMap[T]> => {
        const isNonEmptySkillIdList = is.nonEmptyArray(potentialSkillIds);

        const params = new URLSearchParams({ countOnly: 'true' });

        if (isNonEmptySkillIdList) {
            const skillIds = potentialSkillIds.join(',');
            params.set('skillIds', skillIds);
        }

        const searchParams = params.toString();

        const result = await validatingCobrainerApiFetcher.get(pathnameSchemaMap[pathname], {
            pathname,
            requestOptions: {
                searchParams,
            },
        });

        if (result.isOk) {
            return result.value as PathnameResponseMap[T];
        }

        throw result.error;
    };

export const useGlobalSearchEmployeeCountBySkillIds = ({
    enabled,
    skillIds,
}: {
    readonly enabled: boolean;
    readonly skillIds: readonly string[];
}): UseQueryResult<PaginationCountResponse> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const pathname = `/global/skill-id/users`;
    const queryKey: QueryKey = [pathname, 'countOnly', skillIds];

    return useQuery({
        enabled: enabled && is.nonEmptyArray(skillIds),
        queryFn: createSkillIdsCountQueryFunction(pathname, skillIds, validatingCobrainerApiFetcher),
        queryKey,
    });
};

export const useGlobalSearchLearningOpportunitiesCountBySkillIds = ({
    enabled,
    skillIds,
}: {
    readonly enabled: boolean;
    readonly skillIds: readonly string[];
}): UseQueryResult<PaginationCountResponse> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const pathname = `/global/skill-id/learning-opportunities`;
    const queryKey: QueryKey = [pathname, 'countOnly', skillIds];

    return useQuery({
        enabled: enabled && is.nonEmptyArray(skillIds),
        queryFn: createSkillIdsCountQueryFunction(pathname, skillIds, validatingCobrainerApiFetcher),
        queryKey,
    });
};

export const useGlobalSearchSkillsCountBySkillIds = ({
    enabled,
    skillIds,
}: {
    readonly enabled: boolean;
    readonly skillIds: readonly string[];
}): UseQueryResult<RelatedSkillsResponse> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const pathname = `/global/skill-id/skills`;
    const queryKey: QueryKey = [pathname, 'countOnly', skillIds];

    return useQuery({
        enabled: enabled && is.nonEmptyArray(skillIds),
        queryFn: createSkillIdsCountQueryFunction(pathname, skillIds, validatingCobrainerApiFetcher),
        queryKey,
    });
};

export const useGlobalSearchJobsCountBySkillsIds = ({
    enabled,
    skillIds,
}: {
    readonly enabled: boolean;
    readonly skillIds: readonly string[];
}): UseQueryResult<PaginationCountResponse> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const pathname = `/global/skill-id/jobs`;
    const queryKey: QueryKey = [pathname, 'countOnly', skillIds];

    return useQuery({
        enabled: enabled && is.nonEmptyArray(skillIds),
        queryFn: createSkillIdsCountQueryFunction(pathname, skillIds, validatingCobrainerApiFetcher),
        queryKey,
    });
};

export const useGlobalSearchSearchTermCountQueries = (
    searchTerm: string,
): ReadonlyArray<UseQueryResult<PaginationCountResponse>> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const { data: globalPermissions, isSuccess } = useGlobalPermissions();

    const searchParams = {
        countOnly: true,
        searchTerm,
    };

    const defaultCountPaths: readonly Pathname[] = [
        '/global/search/skills',
        '/global/search/users',
        '/global/search/jobs',
    ];

    const pathnames: readonly Pathname[] =
        isSuccess && globalPermissions.canSearchLearningOpportunities
            ? [...defaultCountPaths, '/global/search/learning-opportunities']
            : defaultCountPaths;

    return useQueries({
        queries: pathnames.map((pathname) => ({
            enabled: globalSearchTermSchema.safeParse(searchTerm).success && isSuccess,
            queryFn: async () => {
                const result = await validatingCobrainerApiFetcher.get(paginationCountResponseSchema, {
                    pathname,
                    requestOptions: {
                        searchParams,
                    },
                });

                if (result.isOk) {
                    return result.value;
                }

                throw result.error;
            },
            queryKey: [pathname, 'get', paginationCountResponseSchema, searchTerm, searchParams],
            throwOnError: false,
        })),
    });
};
